/**
 * IonHome — empty-state landing shown in the `full` geometry when there is
 * no active conversation yet (chatMessages.value.length === 0). It replaces
 * ONLY the message list inside `.ion-panel__body` (see IonSurface) — the
 * footer ChatInput stays mounted underneath exactly as before, so this is a
 * message-list swap, not a replacement of the whole chat+canvas split; the
 * canvas aside and geometry logic in IonSurface are untouched.
 *
 * TWO BLOCKS, ONE GRID (2026-08-05, BA)
 * =====================================
 * The page used to stack four: hero, a "Find the right cell" tile, a row of
 * three quiet links, and a row of four prompt chips. BA asked for it to be as
 * plain as the capability gallery — "wenn ich auf See what Ion can do klicke,
 * komme ich auf das Grid, und so übersichtlich soll es sein" — so the last
 * three blocks collapse into one card grid that borrows the gallery's Kachel
 * treatment. Same axis as the gallery, too: each card is one thing Ion can do.
 *
 * EVERY CARD PUTS ION IN THE LOOP
 * ===============================
 * The old links navigated and left. Clicking "Explore the catalogue" dropped
 * you on a bare Explorer with Ion nowhere in sight, which taught exactly the
 * wrong lesson — BA: "hier soll irgendwie Explore the Catalogue mit ein, oder
 * wie kann mir Compare Cells helfen? Also überall soll es mit drin sein."
 * So a feature card now DOCKS Ion beside the feature and starts it on a real
 * prompt, using the same collapse+navigate move IonCapabilityGallery makes
 * after a steering replay. The user sees the feature and Ion working in it,
 * which is how the product is meant to be used.
 *
 * The four prompt chips are gone with the links: "Plot it" and "Explain a
 * concept" are the same offers as cards, and they now say what they are for
 * instead of showing one sample sentence each.
 *
 * "Find the right cell" and its branch chips were REMOVED on 2026-08-27: they
 * were the last entry point into the AI-guided cell-selection flow, which is
 * switched off (`GUIDED_SELECTION_ENABLED = false`, see config/featureFlags.js).
 * The card still seeded the flow via `seedGuidedSelection`, so it led into an
 * unfinished workflow that nothing else exposes any more. Cell selection now
 * starts from the classic Advanced filter+ranking wizard at /cell-selection.
 * `CellSelectionEntry` keeps `BRANCHES` / `useSelectionBranches` for its own
 * (currently flag-disabled) Guided door — do not delete those from there.
 *
 * Earlier decluttering (A1) moved "Recent conversations" to the left rail
 * (IonSidebar); it stays there — it was a second list of the same thing, and
 * on the landing page only, so it vanished the moment you asked anything.
 */
import { useLocation } from 'wouter-preact'
import {
  autoSendDraft,
  clearConversation,
  collapseFromFull,
  draftInput,
  ionArrivedFresh,
} from '../../state/chatStore.js'
import { getChatContextHint, getChatUserName } from '../../modules/chatContextExtractor.js'
import { openCapabilityGallery } from '../../state/capabilityGalleryState.js'
import { appendDemoParam } from '../../services/ionDemoMode.js'
import { IonLockup } from './IonLockup.jsx'
import { IonLanguageLine } from './IonLanguageLine.jsx'
import { t } from '../../i18n/index.js'
import {
  IconGrid,
  IconCompare,
  IconLayers,
  IconScatterPlot,
  IconInfo,
} from '../shared/Icons.jsx'

/**
 * The grid, in the order a first-time user should meet it: steer the app, then
 * read it, then ask about it, then watch it.
 *
 * `route` present → dock Ion and go there first, so the prompt runs where the
 * feature is (Ion's explorer/compare tools refuse to act on a page they are not
 * on — see capture_ion_recording.py's `--page cells` note). `route` absent →
 * the prompt runs right here on /assistant.
 *
 * `prompt` is deliberately a concrete sentence rather than an open invitation:
 * a card that fires "help me explore" teaches nothing about how to talk to Ion,
 * while one that fires a real request shows the shape of a good ask.
 */
const CARDS = [
  {
    key: 'explore',
    Icon: IconGrid,
    title: 'Explore the catalogue',
    desc: 'Ion drives the real Explorer — filters, sort and view — while you watch.',
    route: '/explorer',
    prompt:
      'Take me through the catalogue: filter to the high energy-density cells and sort them by peak power.',
  },
  {
    key: 'compare',
    Icon: IconCompare,
    title: 'Compare cells',
    desc: 'Ion lines up candidates side by side and says what separates them.',
    route: '/compare',
    prompt:
      'Compare the strongest 21700 cells for energy density and tell me what separates them.',
  },
  {
    key: 'plot',
    Icon: IconScatterPlot,
    title: 'Plot it',
    desc: 'Any chart you can name — Ragone, discharge curves, impedance.',
    prompt: 'Draw a Ragone chart of the catalogue and colour it by chemistry.',
  },
  {
    key: 'explain',
    Icon: IconInfo,
    title: 'Explain a concept',
    desc: 'Chemistry, ageing, impedance — and how Batemo measures it.',
    prompt: 'What does the DCIR of a cell tell me, and how does Batemo measure it?',
  },
  {
    key: 'gallery',
    Icon: IconLayers,
    title: 'See what Ion can do',
    desc: 'Recordings of real turns. Costs nothing and can be undone.',
  },
]

/**
 * Local clock, deliberately — a greeting is about where the USER is, not where
 * the server is, and the browser already knows. Boundaries at 05/12/18 keep the
 * common cases right; nobody is served by a "Good morning" at 04:30, so the
 * small hours fall to evening rather than getting a fourth wording.
 */
function timeOfDayGreeting() {
  const h = new Date().getHours()
  if (h >= 5 && h < 12) return t('ion.home.greeting.morning')
  if (h >= 12 && h < 18) return t('ion.home.greeting.afternoon')
  return t('ion.home.greeting.evening')
}

export default function IonHome() {
  const [, navigate] = useLocation()

  // Greet only when this page load (or an explicit "new chat") was an ARRIVAL —
  // see `isFreshArrival` in chatStore.js. On an in-session return to an empty
  // home the neutral "How can I help?" is the honest heading: nothing new
  // started, so a "Good evening" would be theatre.
  //
  // Read via .value inside the component so the heading re-renders when
  // `startNewConversation()` flips it, rather than freezing at mount.
  const greeting = ionArrivedFresh.value ? timeOfDayGreeting() : null
  const userName = getChatUserName()
  const title = greeting
    ? userName
      ? t('ion.home.greeting.named', { greeting, name: userName })
      : greeting
    : t('ion.home.greeting.help')

  /**
   * Start a fresh Ion turn on `prompt`, optionally after docking beside a
   * feature page.
   *
   * The hint for the TARGET page is computed here rather than left to
   * IonSurface. Verified live and it mattered: IonSurface resolves a null hint
   * against `contextHintRef.current`, which is assigned during RENDER, while
   * `autoSendDraft` is a signal whose effect fires synchronously — so the turn
   * went out with the pre-navigation page and Ion opened with "You're currently
   * on the Benchmarking page, so I'll send you to the Explorer", standing on
   * the Explorer. That is the staleness IonSurface's own comment warns about
   * two lines above the ref.
   *
   * `getChatContextHint(route)` is a pure function of the path for the field
   * that decides this (`page`), so passing it explicitly is exact instead of
   * timing-dependent — no waiting a tick and hoping the router got there first.
   * The Explorer-steering tools refuse to run unless `page === 'cells'`, which
   * is precisely what was wrong.
   */
  function startIon(prompt, route) {
    if (route) {
      // Dock so the feature is on screen with Ion beside it — the same move
      // IonCapabilityGallery makes after a steering replay.
      collapseFromFull()
      navigate(appendDemoParam(route))
    }
    // clearConversation() also resets draftInput and autoSendDraft, so the
    // prompt has to be set AFTER it (same ordering as seedGuidedSelection).
    clearConversation()
    draftInput.value = prompt
    autoSendDraft.value = {
      text: prompt,
      // No route → we stay put, and IonSurface's own hint is already right.
      contextHint: route ? getChatContextHint(route) : null,
    }
  }

  function handleCardClick(card) {
    if (card.key === 'gallery') {
      openCapabilityGallery()
      return
    }
    startIon(card.prompt, card.route)
  }

  return (
    <div class="ion-home">
      <div class="ion-home__hero">
        <IonLockup size={34} animated className="ion-home__lockup" />
        <h2 class="ion-home__title">{title}</h2>
        {/* Only on an arrival, and only under the greeting — it is what makes
            "Good evening, Björn" an opening rather than a label. Suppressed on
            an in-session return so navigating back to an empty home doesn't
            re-stage a fresh start the user didn't ask for. */}
        {greeting && <p class="ion-home__tagline">{t('ion.home.greeting.tagline')}</p>}
        {/* The same multilingual line the docked panel shows. It was only ever
            on the narrow surface, which is the wrong place for it: "Ion answers
            in your language" is a fact a FIRST-time user should meet, and
            first-time users land here. */}
        <IonLanguageLine />
      </div>

      <div class="ion-home__grid">
        {CARDS.map((card) => (
          <button
            key={card.key}
            type="button"
            class="ion-home__card ion-home__card--action"
            onClick={() => handleCardClick(card)}
          >
            <card.Icon size={20} className="ion-home__card-icon" />
            <span class="ion-home__card-title">{card.title}</span>
            <span class="ion-home__card-desc">{card.desc}</span>
          </button>
        ))}
      </div>
    </div>
  )
}
